import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import { TasksPageableResponseDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { TasksListSearchFormModel } from '@/views/tasks/list/model/TasksListSearchForm.model.ts';
import { formatTaskSearchDate } from '@/views/tasks/list/utils/useGetTasks.util.ts';

export type GetCustomTasksReq = {
  page: number;
  pageSize: number;
  params: TasksListSearchFormModel;
};

type Return = {
  getResourceTasks: (req: GetCustomTasksReq) => Promise<TasksPageableResponseDto>;
  isError: boolean;
  isLoading: boolean;
};

export const useGetResourceTasks = (params: { resourceId?: string }): Return => {
  const { tasksApi } = useApiClient();

  const { isError, isPending, mutateAsync } = useMutation<
    TasksPageableResponseDto,
    AxiosError,
    GetCustomTasksReq
  >({
    meta: { disableSuccessAlert: true },
    mutationFn: async req => {
      if (!params.resourceId) throw new Error('resourceId is required');
      const res = await tasksApi.searchTasks(req.page, req.pageSize, {
        ...req.params,
        assignedUserId: req.params.assignedUserId?.value,
        contextResourceId: params.resourceId,
        contextResourceTypes: req.params.contextResourceTypes.map(it => it.value),
        creationDateFrom: formatTaskSearchDate(req.params.creationDateFrom),
        creationDateTo: formatTaskSearchDate(req.params.creationDateTo),
        creatorUserId: req.params.creatorUserId?.value,
        dueDateFrom: formatTaskSearchDate(req.params.dueDateFrom),
        dueDateTo: formatTaskSearchDate(req.params.dueDateTo),
        taskStatuses: req.params.taskStatuses.map(it => it.value),
        watchersUserIds: req.params.watchersUserIds?.map(it => it.value),
      });
      return res.data;
    },
    mutationKey: [QueryKeyEnum.RESOURCE_TASKS],
  });

  return {
    getResourceTasks: mutateAsync,
    isError: isError,
    isLoading: isPending,
  };
};
